"use strict";
import "sideEffects";
import "./posthog";
import { ApolloProvider } from "@apollo/client";
import { PortalProvider } from "@tamagui/portal";
import { QueryClientProvider } from "@tanstack/react-query";
import Web3Provider, { Web3ProviderUpdater } from "components/Web3Provider";
import { NewVersionUpdater } from "components/NewVersionUpdater";
import { WebUniswapProvider } from "components/Web3Provider/WebUniswapContext";
import { AssetActivityProvider } from "graphql/data/apollo/AssetActivityProvider";
import { TokenBalancesProvider } from "graphql/data/apollo/TokenBalancesProvider";
import { apolloClient } from "graphql/data/apollo/client";
import { LanguageProvider } from "i18n/LanguageProvider";
import { BlockNumberProvider } from "lib/hooks/useBlockNumber";
import App from "pages/App";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Helmet, HelmetProvider } from "react-helmet-async/lib/index";
import { I18nextProvider } from "react-i18next";
import { configureReanimatedLogger } from "react-native-reanimated";
import { Provider } from "react-redux";
import { BrowserRouter, HashRouter, useLocation } from "react-router-dom";
import store from "state";
import { ActivityStateUpdater } from "state/activity/updater";
import ApplicationUpdater from "state/application/updater";
import FiatOnRampTransactionsUpdater from "state/fiatOnRampTransactions/updater";
import ListsUpdater from "state/lists/updater";
import LogsUpdater from "state/logs/updater";
import { ThemeProvider, ThemedGlobalStyle } from "theme";
import { SystemThemeUpdater, ThemeColorMetaUpdater } from "theme/components/ThemeToggle";
import { TamaguiProvider } from "theme/tamaguiProvider";
import { ReactRouterUrlProvider } from "contexts/UrlContext";
import { SharedQueryClient } from "data/apiClients/SharedQueryClient";
import { LocalizationContextProvider } from "features/language/LocalizationContext";
import i18n from "i18n";
import { UserTokensContextProvider } from "contexts/UserTokensContext";
import { TokenListsContextProvider } from "contexts/TokenListsContext";
import { isTestEnv } from "utilities/src/environment/env";
import { isBrowserRouterEnabled } from "utils/env";
import { unregister as unregisterServiceWorker } from "utils/serviceWorker";
import { getCanonicalUrl } from "utils/urlRoutes";
if (window.ethereum) {
  window.ethereum.autoRefreshOnNetworkChange = false;
}
if (__DEV__ && !isTestEnv()) {
  configureReanimatedLogger({
    strict: false
  });
}
function Updaters() {
  const location = useLocation();
  return <>
      <Helmet>
        <link rel="canonical" href={getCanonicalUrl(location.pathname)} />
      </Helmet>
      <ListsUpdater />
      <NewVersionUpdater />
      <SystemThemeUpdater />
      <ThemeColorMetaUpdater />
      <ApplicationUpdater />
      <ActivityStateUpdater />
      <LogsUpdater />
      <FiatOnRampTransactionsUpdater />
      <Web3ProviderUpdater />
    </>;
}
function GraphqlProviders({ children }) {
  return <ApolloProvider client={apolloClient}>
      <AssetActivityProvider>
        <TokenBalancesProvider>{children}</TokenBalancesProvider>
      </AssetActivityProvider>
    </ApolloProvider>;
}
const container = document.getElementById("root");
const Router = isBrowserRouterEnabled() ? BrowserRouter : HashRouter;
createRoot(container).render(
  <StrictMode>
    <HelmetProvider>
      <ReactRouterUrlProvider>
        <Provider store={store}>
          <TokenListsContextProvider>
          <UserTokensContextProvider>
            <QueryClientProvider client={SharedQueryClient}>
              <Router>
              <I18nextProvider i18n={i18n}>
                <LanguageProvider>
                  <Web3Provider>
                    <WebUniswapProvider>
                      <GraphqlProviders>
                        <LocalizationContextProvider>
                          <BlockNumberProvider>
                            <Updaters />
                            <ThemeProvider>
                              <TamaguiProvider>
                                <PortalProvider>
                                  <ThemedGlobalStyle />
                                  <App />
                                </PortalProvider>
                              </TamaguiProvider>
                            </ThemeProvider>
                          </BlockNumberProvider>
                        </LocalizationContextProvider>
                      </GraphqlProviders>
                    </WebUniswapProvider>
                  </Web3Provider>
                </LanguageProvider>
              </I18nextProvider>
            </Router>
          </QueryClientProvider>
        </UserTokensContextProvider>
        </TokenListsContextProvider>
      </Provider>
      </ReactRouterUrlProvider>
    </HelmetProvider>
  </StrictMode>
);
unregisterServiceWorker();
